/**
 * OrdersSubTab.tsx
 *
 * Aggregated lab ORDERS view for the patient Labs section. Combines three
 * sources into one table:
 *   1. Outbound Quest orders (from the shared patient lab-orders payload).
 *   2. Outbound Vibrant orders (labs_vibrant_orders via /vibrant-orders).
 *   3. Lab-order notes (notes_types.isLab = 2 via /lab-order-notes).
 *
 * Quest rows and Vibrant orders open their detail in the patient-detail right
 * sidebar (with an expand-to-modal affordance) via onPreview; note rows expose
 * their attachment download(s).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { Badge, Button, Spinner } from '@/components/ui';
import {
  getPatientVibrantOrders,
  getPatientLabOrderNotes,
  downloadNoteAttachment,
  type LabResult,
} from '@/services/labs.service';
import type { LabPreviewTarget } from './LabPdfPreviewPanel';
import { vibrantStatusVariant } from './vibrantOrderStatus';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

type OrderRow = {
  key: string;
  source: 'Quest' | 'Vibrant' | 'Note';
  sourceLabel: string;
  identifier: string;
  test: string;
  date: string | null;
  status: React.ReactNode;
  /** Plain-text status (mirrors the Status badge) so sorting matches what's shown. */
  statusText: string;
  onClick?: () => void;
  attachments?: Array<{ attachmentId: number; filename: string }>;
};

type SortKey = 'sourceLabel' | 'identifier' | 'test' | 'date' | 'statusText';
type SortDir = 'asc' | 'desc';

interface OrdersSubTabProps {
  patientId: number;
  results: LabResult[];
  loading: boolean;
  onPreview: (target: LabPreviewTarget) => void;
}

export function OrdersSubTab({ patientId, results, loading, onPreview }: OrdersSubTabProps) {
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { data: vibrantOrders, isLoading: vibrantLoading } = useQuery({
    queryKey: ['patient-vibrant-orders', patientId],
    queryFn: () => getPatientVibrantOrders(patientId),
    staleTime: 60_000,
  });

  const { data: orderNotes, isLoading: notesLoading } = useQuery({
    queryKey: ['patient-lab-order-notes', patientId],
    queryFn: () => getPatientLabOrderNotes(patientId),
    staleTime: 60_000,
  });

  const unsortedRows = useMemo<OrderRow[]>(() => {
    const out: OrderRow[] = [];

    for (const r of results) {
      if (r.source !== 'quest') continue;
      const orderIdNum = r.orderNumber ? Number(r.orderNumber) : 0;
      out.push({
        key: r.rowKey,
        source: 'Quest',
        sourceLabel: 'Quest Diagnostics',
        identifier: r.orderNumber ?? '—',
        test: r.testsSummary ?? '—',
        date: r.orderedAt,
        statusText: r.orcStatus ?? (r.orderFound ? 'Received' : 'Pending'),
        status: r.orcStatus ? (
          <Badge
            variant={r.orcStatus === 'Complete' ? 'success' : r.orcStatus === 'Cancelled' ? 'error' : r.orcStatus.includes('Process') ? 'warning' : 'neutral'}
            size="sm"
          >
            {r.orcStatus}
          </Badge>
        ) : r.orderFound ? (
          <Badge variant="info" size="sm">Received</Badge>
        ) : (
          <Badge variant="warning" size="sm">Pending</Badge>
        ),
        onClick: () => {
          onPreview({
            kind: 'quest-detail',
            labResultId: r.labResultId,
            orderId: r.labResultId === 0 && orderIdNum > 0 ? orderIdNum : undefined,
            title: r.orderNumber ? `Quest Order #${r.orderNumber}` : 'Quest Result',
          });
        },
      });
    }

    for (const o of vibrantOrders ?? []) {
      const label = o.accessionId || String(o.orderId);
      out.push({
        key: `vibrant-order-${o.orderId}`,
        source: 'Vibrant',
        sourceLabel: 'Vibrant Wellness',
        identifier: label,
        test: o.testsSummary ?? '—',
        date: o.sentDate ?? o.createDate,
        statusText: o.statusLabel || 'Unknown',
        status: (
          <Badge variant={vibrantStatusVariant(o.statusKey)} size="sm">
            {o.statusLabel || 'Unknown'}
          </Badge>
        ),
        onClick: () => {
          onPreview({
            kind: 'vibrant-order-detail',
            orderId: o.orderId,
            title: `Vibrant Order ${label}`,
          });
        },
      });
    }

    for (const n of orderNotes ?? []) {
      out.push({
        key: `note-${n.noteId}`,
        source: 'Note',
        sourceLabel: n.source || 'Lab Order',
        identifier: '—',
        test: n.title || 'Lab Order',
        date: n.date,
        statusText: 'Order',
        status: <Badge variant="neutral" size="sm">Order</Badge>,
        attachments: n.attachments,
      });
    }

    return out;
  }, [results, vibrantOrders, orderNotes, onPreview]);

  const rows = useMemo(() => {
    return [...unsortedRows].sort((a, b) => {
      const av = (a[sortBy] ?? '').toString();
      const bv = (b[sortBy] ?? '').toString();
      // Empty values always sort last regardless of direction.
      if (av === '' && bv === '') return 0;
      if (av === '') return 1;
      if (bv === '') return -1;
      const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [unsortedRows, sortBy, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  }

  const isLoading = loading || vibrantLoading || notesLoading;

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  if (rows.length === 0) {
    return <p className="py-4 text-sm text-slate-400 italic">No lab orders found.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-100 text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <SortableHeader label="Source" sortKey="sourceLabel" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[1%] whitespace-nowrap" />
            <SortableHeader label="Order# / Accession" sortKey="identifier" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[1%] whitespace-nowrap" />
            <SortableHeader label="Test / Title" sortKey="test" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
            <SortableHeader label="Date" sortKey="date" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[18%] whitespace-nowrap" />
            <SortableHeader label="Status" sortKey="statusText" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[18%]" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => (
            <tr
              key={row.key}
              className={`align-top ${row.onClick ? 'hover:bg-slate-50 cursor-pointer' : 'hover:bg-slate-50'}`}
              onClick={row.onClick}
            >
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{row.sourceLabel}</td>
              <td className="px-4 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">{row.identifier}</td>
              <td className="px-4 py-3 text-slate-800 [overflow-wrap:anywhere] min-w-0">{row.test}</td>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(row.date)}</td>
              <td className="px-4 py-3">
                {row.attachments && row.attachments.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1">
                    {row.status}
                    {row.attachments.map((att) => (
                      <Button
                        key={att.attachmentId}
                        size="xs"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          void downloadNoteAttachment(patientId, att.attachmentId, att.filename);
                        }}
                      >
                        {att.filename ? att.filename.split('.').pop()?.toUpperCase() || 'File' : 'File'}
                      </Button>
                    ))}
                  </div>
                ) : (
                  row.status
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortableHeader({
  label,
  sortKey,
  sortBy,
  sortDir,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sortBy: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sortBy === sortKey;
  return (
    <th className={`px-4 py-3 ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`group inline-flex items-center gap-1 uppercase tracking-wide transition-colors ${
          active ? 'text-slate-700' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400" />
        )}
      </button>
    </th>
  );
}
